import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cross, BookOpen, Users, GraduationCap,
  ArrowRight, CheckCircle2, MessageSquare,
  ShieldCheck, Globe, Library, Sparkles, Plus,
  ScrollText, Church as ChurchIcon, HeartHandshake,
  Compass, History, Menu, X
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { createClientComponentClient } from '@/integrations/supabase/client';
import SEO from '@/components/layout/SEO';

const GOLD = '#c8a84b';
const DARK = '#0f172a'; // slate-900

const getChurchSubjects = (t: any) => [
  { icon: <Cross size={24} />, name: t('church.landing.churchSubjects.0.name'), desc: t('church.landing.churchSubjects.0.desc') },
  { icon: <BookOpen size={24} />, name: t('church.landing.churchSubjects.1.name'), desc: t('church.landing.churchSubjects.1.desc') },
  { icon: <Sparkles size={24} />, name: t('church.landing.churchSubjects.2.name'), desc: t('church.landing.churchSubjects.2.desc') },
  { icon: <ShieldCheck size={24} />, name: t('church.landing.churchSubjects.3.name'), desc: t('church.landing.churchSubjects.3.desc') },
  { icon: <HeartHandshake size={24} />, name: t('church.landing.churchSubjects.4.name'), desc: t('church.landing.churchSubjects.4.desc') },
  { icon: <History size={24} />, name: t('church.landing.churchSubjects.5.name'), desc: t('church.landing.churchSubjects.5.desc') },
  { icon: <Library size={24} />, name: t('church.landing.churchSubjects.6.name'), desc: t('church.landing.churchSubjects.6.desc') },
  { icon: <ScrollText size={24} />, name: t('church.landing.churchSubjects.7.name'), desc: t('church.landing.churchSubjects.7.desc') },
  { icon: <Compass size={24} />, name: t('church.landing.churchSubjects.8.name'), desc: t('church.landing.churchSubjects.8.desc') },
];

const getUseCases = (t: any) => [
  { icon: <Users className="text-sky-500" />, title: t('church.landing.useCases.0.title'), desc: t('church.landing.useCases.0.desc') },
  { icon: <HeartHandshake className="text-sky-500" />, title: t('church.landing.useCases.1.title'), desc: t('church.landing.useCases.1.desc') },
  { icon: <GraduationCap className="text-sky-500" />, title: t('church.landing.useCases.2.title'), desc: t('church.landing.useCases.2.desc') },
  { icon: <ChurchIcon className="text-sky-500" />, title: t('church.landing.useCases.3.title'), desc: t('church.landing.useCases.3.desc') },
];

export default function ChurchLandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [pricing, setPricing] = useState({
    seat: '7',
  });
  const supabase = createClientComponentClient();

  useEffect(() => {
    async function fetchPricing() {
      const { data } = await supabase
        .from('subscription_plans' as any)
        .select('price_monthly')
        .eq('plan_key', 'sage-church-seat')
        .maybeSingle();

      if (data) {
        const d = data as any;
        setPricing({
          seat: Math.round(Number(d.price_monthly) || 7).toString(),
        });
      }
    }
    fetchPricing();
  }, [supabase]);

  const faqs = [
    { q: t('church.landing.faq1q'), a: t('church.landing.faq1a') },
    { q: t('church.landing.faq2q'), a: t('church.landing.faq2a') },
    { q: t('church.landing.faq3q'), a: t('church.landing.faq3a') },
    { q: t('church.landing.faq4q'), a: t('church.landing.faq4a') },
    { q: t('church.landing.faq5q'), a: t('church.landing.faq5a') },
  ];

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-100 font-sans selection:bg-amber-500/30 overflow-x-hidden">
      <SEO
        title={t('church.landing.heroTitle1') + ' ' + t('church.landing.heroTitle2')}
        description={t('church.landing.heroSubtitle')}
        url="https://stillwatersacademy.com/church"
      />

      {/* NAV */}
      <nav className="sticky top-[80px] z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800/50 py-4 px-4 md:px-12 flex justify-between items-center">
        <Link to="/" onClick={() => setNavOpen(false)} className="flex items-center gap-2.5 group shrink-0">
          <div className="w-9 h-9 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20 group-hover:scale-105 transition-transform">
            <span className="text-white font-black text-lg italic">S</span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-lg tracking-tight leading-none text-white whitespace-nowrap">Still Waters</span>
            <span className="text-[10px] uppercase tracking-widest font-black text-amber-500/80 whitespace-nowrap">{t('church.nav.churchEdition')}</span>
          </div>
        </Link>

        {/* Desktop nav links + CTA */}
        <div className="hidden md:flex items-center gap-8 text-sm font-bold text-slate-400">
          <Link to="/homeschool" className="hover:text-amber-500 transition-colors">{t('church.nav.homeschool')}</Link>
          <Link to="/learn" className="hover:text-amber-500 transition-colors">{t('church.nav.education')}</Link>
          <Button
            onClick={() => navigate('/church/register')}
            className="bg-amber-500 hover:bg-amber-600 text-white font-black px-6 rounded-full shadow-lg shadow-amber-500/20"
          >
            {t('church.landing.registerCTA')}
          </Button>
        </div>

        {/* Mobile direct CTA */}
        <div className="flex md:hidden items-center gap-2">
          <Button
            onClick={() => navigate('/church/register')}
            className="bg-amber-500 hover:bg-amber-600 text-white font-black px-4 py-1.5 text-xs rounded-full shadow-md"
          >
            {t('church.landing.registerCTA', 'Register')} →
          </Button>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="relative pt-20 pb-24 px-6 overflow-hidden">
        {/* Abstract Background Shapes */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[140%] h-[140%] bg-amber-500/5 rounded-full blur-[120px] -z-10" />

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 font-black text-xs tracking-widest uppercase shadow-sm">
              <Sparkles size={14} />
              <span>{t('church.landing.forChurches')}</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.05] tracking-tight">
              {t('church.landing.heroTitle1')}<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600 italic">
                {t('church.landing.heroTitle2')}
              </span><br />
              {t('church.landing.heroTitle3')}
            </h1>

            <p className="text-xl text-slate-400 max-w-xl leading-relaxed font-medium">
              {t('church.landing.heroSubtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                size="xl"
                onClick={() => navigate('/church/register')}
                className="h-16 px-10 text-lg font-black bg-amber-500 hover:bg-amber-600 text-white rounded-2xl shadow-xl shadow-amber-500/20 active:scale-95 transition-all"
              >
                {t('church.landing.registerCTA')} <ArrowRight className="ml-2" />
              </Button>
              <Button
                size="xl"
                variant="outline"
                onClick={() => navigate('/learn')}
                className="h-16 px-10 text-lg font-bold border-2 border-slate-700 text-white hover:bg-slate-800 rounded-2xl transition-colors bg-transparent shadow-lg"
              >
                {t('church.landing.previewCourses')}
              </Button>
            </div>

            <div className="pt-8 flex gap-12 border-t border-slate-800/50 w-fit">
              {[
                { val: '15+', label: t('church.landing.statsLabel1') },
                { val: '100s', label: t('church.landing.curriculumHighlight') },
                { val: `$${pricing.seat}`, label: t('church.landing.statsLabel2') }
              ].map((stat, i) => (
                <div key={i} className="flex flex-col">
                  <span className="text-3xl font-black text-amber-500">{stat.val}</span>
                  <span className="text-xs uppercase tracking-widest font-bold text-slate-400 mt-1">{stat.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Interactive Chat Preview */}
          <motion.div
            initial={{ opacity: 0, x: 30, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block relative"
          >
            <div className="bg-slate-900/40 border border-white/5 rounded-[2.5rem] p-4 backdrop-blur-2xl shadow-2xl relative z-10">
              <div className="bg-slate-800/40 rounded-[2rem] overflow-hidden border border-white/5">
                <div className="bg-amber-500/10 border-b border-amber-500/20 p-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                      <BookOpen size={20} className="text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-sm">{t('church.landing.chat.title')}</h4>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-amber-500/70">{t('church.landing.chat.module')}</p>
                    </div>
                  </div>
                  <div className="px-3 py-1 bg-amber-500/20 rounded-full border border-amber-500/30 text-[10px] font-black text-amber-500 uppercase tracking-tighter">{t('church.landing.chat.live')}</div>
                </div>

                <div className="p-6 space-y-6 min-h-[300px]">
                  <div className="flex gap-4">
                    <div className="w-9 h-9 bg-amber-500 rounded-lg flex-shrink-0 flex items-center justify-center font-black text-white shadow-lg shadow-amber-500/20">S</div>
                    <div className="bg-slate-800/60 p-5 rounded-2xl rounded-tl-none border border-white/5 text-sm text-slate-300 leading-relaxed max-w-[85%]">
                      <span className="font-bold text-sky-500 block mb-1">{t('church.landing.chat.sage')}</span>
                      {t('church.landing.chat.sageMsg')}
                    </div>
                  </div>

                  <div className="flex gap-4 flex-row-reverse">
                    <div className="w-9 h-9 bg-slate-700 rounded-lg flex-shrink-0 flex items-center justify-center text-lg">👨‍💻</div>
                    <div className="bg-amber-500/10 p-5 rounded-2xl rounded-tr-none border border-amber-500/20 text-sm text-amber-100 font-medium leading-relaxed max-w-[85%]">
                      {t('church.landing.chat.userMsg')}
                    </div>
                  </div>
                </div>

                <div className="p-4 border-t border-white/5 flex justify-between items-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  <div className="flex items-center gap-2">
                    <Users size={12} className="text-amber-500" /> {t('church.landing.chat.active', { count: 12 })}
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={12} className="text-amber-500" /> {t('church.landing.chat.mastery', { score: 78 })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* USE CASES SECTION */}
      <section className="bg-slate-900/50 py-24 px-6 border-y border-white/5 backdrop-blur-3xl">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">{t('church.landing.howTitle')}</h2>
            <div className="h-1.5 w-24 bg-amber-500 mx-auto rounded-full" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {getUseCases(t).map((uc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -8, backgroundColor: 'rgba(255,255,255,0.03)' }}
                className="bg-white/5 border border-white/5 p-8 rounded-[2rem] transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-slate-800 rounded-2xl flex items-center justify-center mb-6 text-2xl shadow-xl border border-white/5 group-hover:bg-amber-500 group-hover:text-white transition-colors">
                  {uc.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-4 leading-tight">{uc.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed font-medium">{uc.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SPONSORSHIP & GIFTING SECTION */}
      <section className="py-32 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500">
                <HeartHandshake size={20} />
                <span className="text-xs font-black uppercase tracking-widest">{t('church.landing.sponsorship.title', 'Gifting & Outreach')}</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-black text-white tracking-tighter leading-none">
                {t('church.landing.sponsorship.gifting')} <br />
                <span className="text-amber-500 italic">{t('church.landing.curriculumHighlightStats.growth')}</span>
              </h2>
              <p className="text-xl text-slate-400 font-medium leading-relaxed max-w-xl">
                {t('church.landing.sponsorship.description')}
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-2xl flex items-center justify-center text-amber-500 mb-4">
                    <Users size={24} />
                  </div>
                  <h4 className="font-bold text-white mb-2">{t('church.landing.sponsorship.outreach')}</h4>
                  <p className="text-sm text-slate-400 leading-relaxed">{t('church.landing.sponsorship.outreachDesc')}</p>
                </div>
                <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-2xl flex items-center justify-center text-amber-500 mb-4">
                    <HeartHandshake size={24} />
                  </div>
                  <h4 className="font-bold text-white mb-2">{t('church.landing.sponsorship.gifting')}</h4>
                  <p className="text-sm text-slate-400 leading-relaxed">{t('church.landing.sponsorship.giftingDesc')}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-amber-500/10 blur-3xl rounded-full opacity-50" />
              <div className="bg-gradient-to-br from-slate-900 to-slate-950 border border-white/10 p-10 rounded-[3rem] shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-[0.05] group-hover:scale-110 transition-transform duration-500">
                  <HeartHandshake size={200} className="text-amber-500" />
                </div>

                <h3 className="text-2xl font-black text-white mb-8">{t('church.landing.sponsorshipCard.title')}</h3>
                <div className="space-y-6 mb-10">
                  <div className="h-24 bg-white/5 rounded-2xl border border-white/5 p-6 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('church.landing.sponsorshipCard.purchased')}</div>
                      <div className="text-3xl font-black text-white">12</div>
                    </div>
                    <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center text-white font-black text-lg shadow-lg shadow-amber-500/20">+</div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-widest px-2">
                      <span>{t('church.landing.sponsorshipCard.usage')}</span>
                      <span>{t('church.landing.sponsorshipCard.active', { active: 8, total: 12 })}</span>
                    </div>
                    <div className="h-3 bg-white/5 rounded-full overflow-hidden p-0.5 border border-white/5">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: '66%' }}
                        className="h-full bg-amber-500 rounded-full shadow-[0_0_10px_rgba(245,158,11,0.5)]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-5 gap-2">
                    {[1, 2, 3, 4, 1].map((_, i) => (
                      <div key={i} className={`h-10 rounded-lg flex items-center justify-center font-bold text-xs ${i < 3 ? 'bg-amber-500/20 text-amber-500 border border-amber-500/30' : 'bg-white/5 text-slate-400 border border-white/5'}`}>
                        {i < 3 ? '✓' : ''}
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={() => navigate('/church/register')}
                  className="w-full h-16 bg-white hover:bg-slate-100 text-slate-950 font-black text-lg rounded-2xl"
                >
                  {t('church.landing.sponsorshipCard.cta')}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CURRICULUM HIGHLIGHT SECTION */}
      <section className="py-24 px-6 bg-slate-950/50">
        <div className="max-w-7xl mx-auto">
          <div className="p-12 md:p-20 bg-gradient-to-br from-slate-900/50 to-slate-950/50 rounded-[4rem] border border-white/5 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-12 opacity-[0.02] pointer-events-none group-hover:scale-105 transition-transform duration-700">
              <ScrollText size={500} className="text-amber-500" />
            </div>

            <div className="relative z-10 grid md:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <h2 className="text-4xl md:text-5xl font-black text-white tracking-tighter leading-none">
                  {t('church.landing.curriculumHighlightTitle')}
                </h2>
                <p className="text-lg text-slate-400 font-medium leading-relaxed">
                  {t('church.landing.coursesSubtitle')}
                </p>
                <Button
                  variant="outline"
                  className="h-14 border-amber-500/30 text-amber-500 hover:bg-amber-500/10 rounded-2xl"
                  onClick={() => navigate('/church/register')}
                >
                  {t('church.landing.curriculumHighlightCta')}
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-2">
                  <div className="text-5xl font-black text-white italic">15</div>
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{t('church.landing.statsLabel1')}</div>
                </div>
                <div className="space-y-2">
                  <div className="text-5xl font-black text-white italic">100s</div>
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{t('church.landing.curriculumHighlightDetailed')}</div>
                </div>
                <div className="space-y-2">
                  <div className="text-5xl font-black text-white italic">8</div>
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{t('church.landing.curriculumHighlightStats.mentors')}</div>
                </div>
                <div className="space-y-2">
                  <div className="text-5xl font-black text-white italic">∞</div>
                  <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{t('church.landing.curriculumHighlightStats.growth')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CURRICULUM GRID */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 space-y-6">
            <h2 className="text-5xl md:text-7xl font-black text-white tracking-tighter leading-none italic">
              {t('church.landing.coursesTitle')}
            </h2>
            <p className="text-xl text-slate-400 font-medium max-w-2xl mx-auto uppercase tracking-wide">
              {t('church.landing.coursesSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {getChurchSubjects(t).map((subject: any, idx: number) => (
              <motion.div
                key={idx}
                className="p-8 bg-slate-900/40 border border-white/5 rounded-[2.5rem] group hover:bg-slate-900/60 hover:border-amber-500/20 transition-all duration-300 cursor-pointer"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => navigate('/church/register')}
              >
                <div className="w-14 h-14 bg-amber-500/10 rounded-2xl flex items-center justify-center text-amber-500 mb-6 group-hover:scale-110 group-hover:bg-amber-500 group-hover:text-white transition-all duration-300 shadow-lg shadow-amber-500/0 group-hover:shadow-amber-500/20">
                  {subject.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3 tracking-tight group-hover:text-amber-500 transition-colors uppercase">{subject.name}</h3>
                <p className="text-slate-400 text-sm leading-relaxed group-hover:text-slate-400 transition-colors">{subject.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Button
              variant="outline"
              size="lg"
              className="px-10 h-16 text-sm font-black uppercase tracking-widest border-slate-800 hover:bg-white hover:text-slate-950 rounded-2xl transition-all"
              onClick={() => navigate('/church/register')}
            >
              + {t('church.landing.curriculumHighlightCta')}
            </Button>
          </div>
        </div>
      </section>

      {/* LANGUAGES SECTION */}
      <section className="py-24 px-6 relative overflow-hidden bg-slate-950/20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-12 md:p-20 bg-slate-900/40 rounded-[4rem] border border-sky-500/10 backdrop-blur-sm relative group text-center"
          >
            <div className="absolute top-0 right-0 p-12 opacity-[0.05] group-hover:scale-110 transition-transform">
              <Globe size={300} className="text-sky-500" />
            </div>
            <div className="relative z-10 max-w-3xl mx-auto space-y-8">
              <h3 className="text-4xl md:text-6xl font-black text-white italic">{t('church.landing.languagesTitle')}</h3>
              <p className="text-xl text-slate-400 font-medium leading-relaxed">
                {t('church.landing.languagesText')}
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {(t('church.languages', { returnObjects: true }) as string[])?.map((lang: string) => (
                  <span key={lang} className="px-5 py-2 rounded-full bg-sky-500/10 border border-sky-500/20 text-xs font-black text-sky-500 uppercase tracking-widest">{lang}</span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section className="py-32 px-6 bg-slate-950/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 space-y-4">
            <h2 className="text-6xl md:text-8xl font-black text-white tracking-tighter leading-none italic">{t('church.landing.pricingTitle')}</h2>
            <div className="h-1.5 w-32 bg-amber-500 mx-auto rounded-full mt-6" />
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-stretch max-w-4xl mx-auto">

            {/* CARD 1: INDIVIDUAL / MEMBER */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-900/80 border border-white/5 rounded-[3rem] p-10 flex flex-col relative overflow-hidden group hover:border-sky-500/20 transition-all shadow-2xl"
            >
              <div className="relative z-10 flex flex-col h-full">
                <span className="inline-block self-start px-4 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-[10px] font-black tracking-widest text-sky-500 uppercase mb-8">
                  {t('church.landing.pricing.individual.title')}
                </span>

                <div className="flex flex-col mb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-6xl font-black text-white tracking-tighter italic">$</span>
                    <span className="text-7xl font-black text-white tracking-tighter">{pricing.seat}</span>
                  </div>
                  <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px] mt-1">{t('church.landing.pricing.monthlyPeriod', 'MONTHLY')}</span>
                </div>

                <p className="text-sm text-slate-400 mb-10 leading-relaxed font-medium">
                  {t('church.landing.pricing.individual.desc')}
                </p>

                <ul className="space-y-4 mb-10 text-left flex-1">
                  {(t('church.landing.pricing.individual.features', { returnObjects: true }) as string[])?.map((feat: string, idx: number) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-300 font-bold text-sm">
                      <CheckCircle2 size={16} className="text-sky-500" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => navigate('/church/register?type=individual')}
                  className="w-full h-16 bg-sky-600 hover:bg-sky-500 text-white font-black text-lg rounded-2xl transition-all shadow-xl"
                >
                  {t('church.landing.pricing.individual.cta')}
                </Button>
              </div>
            </motion.div>

            {/* CARD 2: CHURCH CONGREGATION */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-slate-900/80 border border-amber-500/30 rounded-[3rem] p-10 flex flex-col relative overflow-hidden group shadow-2xl shadow-amber-500/10"
            >
              <div className="relative z-10 flex flex-col h-full">
                <span className="inline-block self-start px-4 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-[10px] font-black tracking-widest text-amber-400 uppercase mb-8">
                  CHURCH & MINISTRY
                </span>

                <div className="flex flex-col mb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-6xl font-black text-white tracking-tighter italic">$</span>
                    <span className="text-7xl font-black text-white tracking-tighter">{pricing.seat}</span>
                  </div>
                  <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px] mt-1">/ SEAT / MONTH</span>
                </div>

                <p className="text-sm text-slate-400 mb-10 leading-relaxed font-medium">
                  Free church registration with pastor dashboard. Sponsor or assign seats to your congregation as needed.
                </p>

                <ul className="space-y-4 mb-10 text-left flex-1">
                  {[
                    'Free church hub & registration',
                    'Pastor & ministry admin dashboard',
                    'Member spiritual progress tracking',
                    'Sponsor & assign seats for members',
                    'Group study tools & curriculum access'
                  ].map((feat, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-300 font-bold text-sm">
                      <CheckCircle2 size={16} className="text-amber-500" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => navigate('/church/register?type=church')}
                  className="w-full h-16 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-lg rounded-2xl shadow-xl transition-all"
                >
                  {t('church.landing.pricing.congregation.cta')}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-32 px-6 max-w-4xl mx-auto">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">{t('church.landing.faqTitle')}</h2>
          <div className="h-1.5 w-24 bg-amber-500 mx-auto rounded-full" />
        </div>

        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              className="bg-slate-900/40 border border-white/5 rounded-[2rem] overflow-hidden backdrop-blur-sm"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full p-8 flex items-center justify-between text-left group"
              >
                <span className="text-lg font-bold text-slate-200 group-hover:text-amber-500 transition-colors pr-8 leading-snug">{faq.q}</span>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${openFaq === i ? 'bg-amber-500 text-white rotate-45' : 'bg-slate-800 text-slate-400 group-hover:bg-slate-700'}`}>
                  <Plus size={20} strokeWidth={3} />
                </div>
              </button>
              <AnimatePresence>
                {openFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <div className="px-8 pb-8 text-slate-400 font-medium leading-relaxed border-t border-white/5 pt-6 mx-5">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SUPPORT US SECTION - MORE PROMINENT */}
      <section className="py-24 px-6 relative bg-gradient-to-b from-transparent to-amber-500/10">
        <div className="max-w-4xl mx-auto text-center space-y-10">
          <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-500 mb-4">
            <HeartHandshake size={24} />
            <span className="text-sm font-black uppercase tracking-[0.2em]">{t('church.landing.supportTitle', 'Support Our Mission')}</span>
          </div>
          <p className="text-2xl md:text-3xl text-slate-200 font-bold leading-relaxed italic">
            "{t('church.landing.supportText')}"
          </p>
          <div className="h-px w-32 bg-amber-500/30 mx-auto" />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 px-6 text-center relative overflow-hidden bg-amber-500">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-400/50 via-transparent to-transparent pointer-events-none" />

        <div className="max-w-4xl mx-auto relative z-10 space-y-10 py-12">
          <h2 className="text-5xl md:text-7xl font-black text-white tracking-tighter leading-none">
            {t('church.landing.ctaTitle')}<br />
            <span className="text-white italic">{t('church.landing.ctaTitleEm')}</span>
          </h2>
          <p className="text-xl text-slate-900/70 font-black max-w-xl mx-auto uppercase tracking-wide">
            {t('church.landing.ctaSubtitle')}
          </p>
          <div className="flex flex-col items-center gap-6">
            <Button
              size="xl"
              onClick={() => navigate('/church/register')}
              className="h-20 px-16 text-2xl font-black bg-slate-900 hover:bg-slate-800 text-amber-500 rounded-[2.5rem] shadow-2xl shadow-black/20 active:scale-95 transition-all"
            >
              {t('church.landing.registerCTA')} <ArrowRight className="ml-3" size={28} />
            </Button>
            <div className="text-sm font-black text-slate-900/60 uppercase tracking-[0.2em]">
              {t('church.landing.ctaBadge')}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER MINI */}
      <footer className="py-12 px-6 border-t border-white/5 bg-slate-950 flex flex-col md:flex-row justify-between items-center gap-8 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 opacity-50">
          <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center font-bold text-sm">S</div>
          <span className="text-xs font-bold tracking-widest uppercase">{t('church.footer.copyright')}</span>
        </div>
        <div className="flex gap-8 text-[10px] font-black uppercase tracking-widest text-slate-400">
          <Link to="/privacy" className="hover:text-amber-500 transition-colors">{t('church.nav.privacy')}</Link>
          <Link to="/terms" className="hover:text-amber-500 transition-colors">{t('church.nav.terms')}</Link>
          <Link to="/contact" className="hover:text-amber-500 transition-colors">{t('church.nav.contact')}</Link>
        </div>
      </footer>
    </div>
  );
}
